<template>
    <div class="self-stretch flex flex-col justify-start items-center gap-8" data-testid="session-info">
      <!-- Title Section -->
      <div class="self-stretch flex flex-col justify-start items-start gap-6">
        <div class="self-stretch flex flex-col justify-start items-center gap-1">
          <div class="self-stretch text-center text-white text-2xl font-bold" data-testid="session-info-title">
            {{ $t('invite_others') }}
          </div>
          <span class="text-text-neutral-high text-sm text-center mt-1">{{ $t('no_need_acc_to_join') }}</span>
        </div>

        <!-- QR Code and Room Code Section -->
        <div class="self-stretch flex flex-col justify-start items-center gap-2">
          <div class="self-stretch flex flex-col justify-start items-center gap-4">
            <div class="w-[223px] h-[223px] rounded-[11px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] bg-white p-2 flex items-center justify-center">
              <qrcode-vue :value="qr_link" :size="200" level="H" :margin="0" />
            </div>
            <div class="flex items-center gap-2">
              <div class="justify-end text-white/90 text-base font-medium font-['Inter'] leading-normal" data-testid="session-info-code">
                {{ $t('share_room_session_code') }}: {{ formattedRoomCode }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Buttons Section -->
      <div class="self-stretch flex flex-col justify-start items-start gap-3">
        <!-- Share Invite Button -->
        <button
          @click="shareInvite"
          class="self-stretch px-5 py-2.5 bg-[#6CBFFF] hover:bg-opacity-30 rounded-xl inline-flex justify-center items-center gap-2 overflow-hidden transition duration-160 cursor-pointer"
          data-testid="session-info-share-button"
        >
          <div class="relative overflow-hidden">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <g clip-path="url(#clip0_2464_12018)">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M7.5286 1.5286C7.78895 1.26825 8.21106 1.26825 8.47141 1.5286L11.1381 4.19526C11.3984 4.45561 11.3984 4.87772 11.1381 5.13807C10.8777 5.39842 10.4556 5.39842 10.1953 5.13807L8.66667 3.60948V10C8.66667 10.3682 8.36819 10.6667 8 10.6667C7.63181 10.6667 7.33333 10.3682 7.33333 10V3.60948L5.80474 5.13807C5.54439 5.39842 5.12228 5.39842 4.86193 5.13807C4.60158 4.87772 4.60158 4.45561 4.86193 4.19526L7.5286 1.5286ZM2 7.33333C2.36819 7.33333 2.66667 7.63181 2.66667 8V10.8C2.66667 11.3711 2.66719 11.7592 2.6917 12.0593C2.71558 12.3516 2.75886 12.5011 2.81199 12.6053C2.93982 12.8562 3.1438 13.0602 3.39468 13.188C3.49895 13.2411 3.64841 13.2844 3.94069 13.3083C4.24075 13.3328 4.62895 13.3333 5.2 13.3333H10.8C11.3711 13.3333 11.7592 13.3328 12.0593 13.3083C12.3516 13.2844 12.5011 13.2411 12.6053 13.188C12.8562 13.0602 13.0602 12.8562 13.188 12.6053C13.2411 12.5011 13.2844 12.3516 13.3083 12.0593C13.3328 11.7592 13.3333 11.3711 13.3333 10.8V8C13.3333 7.63181 13.6318 7.33333 14 7.33333C14.3682 7.33333 14.6667 7.63181 14.6667 8V10.8276C14.6667 11.3642 14.6667 11.8071 14.6372 12.1679C14.6066 12.5426 14.5409 12.8871 14.376 13.2106C14.1204 13.7124 13.7124 14.1204 13.2106 14.376C12.8871 14.5409 12.5426 14.6066 12.1679 14.6372C11.8071 14.6667 11.3642 14.6667 10.8276 14.6667H5.17245C4.63581 14.6667 4.19291 14.6667 3.83212 14.6372C3.45738 14.6066 3.11292 14.5409 2.78936 14.376C2.28759 14.1204 1.87964 13.7124 1.62398 13.2106C1.45912 12.8871 1.39341 12.5426 1.3628 12.1679C1.33332 11.8071 1.33332 11.3642 1.33333 10.8275L1.33333 8C1.33333 7.63181 1.63181 7.33333 2 7.33333Z" fill="black"/>
              </g>
              <defs>
                <clipPath id="clip0_2464_12018">
                  <rect width="16" height="16" fill="white"/>
                </clipPath>
              </defs>
            </svg>
          </div>
          <div class="justify-start text-black text-base font-medium font-['Inter'] leading-normal">
            {{ $t('share_invite') || 'Share invite' }}
          </div>
        </button>

        <!-- Copy Link Button -->
        <button
          @click="copyLink"
          class="self-stretch px-5 py-2.5 bg-[#6CBFFF]/20 hover:bg-[#6CBFFF]/30 rounded-xl inline-flex justify-center items-center gap-2 overflow-hidden transition duration-160 cursor-pointer"
          data-testid="session-info-copy-link-button"
        >
          <LinkIcon class="size-4 text-[#6CBFFF] shrink-0" />
          <div class="justify-start text-[#6CBFFF] text-base font-medium font-['Inter'] leading-normal">
            {{ $te('copy_link') ? $t('copy_link') : 'Copy link' }}
          </div>
        </button>

        <!-- Cancel -->
        <button
          type="button"
          @click="$emit('back')"
          class="self-stretch py-2.5 inline-flex justify-center items-center text-[#6CBFFF] text-base font-medium font-['Inter'] leading-normal hover:opacity-80 transition cursor-pointer"
          data-testid="session-info-cancel-button"
        >
          {{ $t('common_cancel') || 'Cancel' }}
        </button>
      </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import QrcodeVue from 'qrcode.vue';
import QRService from "@/services/qr.service";
import { LinkIcon } from '@heroicons/vue/24/outline';

export default {
  name: 'SessionInfo',
  emits: ['back'],
  props: {
    room_code: {
      type: String,
      required: true
    }
  },
  components: {
    QrcodeVue,
    LinkIcon,
  },
  data() {
    return {
      shareSupported: false
    }
  },
  computed: {
    ...mapState('event', ['event']),
    qr_link() {
      return QRService.generateQRLink(this.room_code);
    },
    formattedRoomCode() {
      // Format the room code as XXX-XXX
      if (this.room_code && this.room_code.length === 6) {
        return `${this.room_code.substring(0, 3)}-${this.room_code.substring(3)}`;
      }
      return this.room_code;
    }
  },
  methods: {
    clipboardCopy() {
      navigator.clipboard.writeText(this.room_code);
      const message = this.$t ? this.$t('code_copy_clipboard_complete') : 'Copied to clipboard!';
      this.$toast.success(message);
    },
    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.qr_link);
        const message = this.$te('link_copy_clipboard_complete')
          ? this.$t('link_copy_clipboard_complete')
          : 'Link to Session copied to clipboard';
        this.$toast.success(message);
      } catch (error) {
        console.error('Error copying link:', error);
        this.$toast.error(this.$t('common_error') || 'Could not copy link');
      }
    },
    async shareInvite() {
      if (this.shareSupported) {
        try {
          const shareData = {
            title: this.$t('share_room_title'),
            text: `${this.$t('share_room_text')} ${this.room_code}`,
            url: this.qr_link
          };

          await navigator.share(shareData);
        } catch (error) {
          console.error('Error sharing:', error);
          // Fallback to clipboard copy if sharing fails
          this.clipboardCopy();
        }
      } else {
        // Fallback for browsers that don't support sharing
        this.clipboardCopy();
      }
    }
  },
  mounted() {
    // Check if Web Share API is supported
    this.shareSupported = navigator && navigator.share;
  },
}
</script>
